// Skyward PM C3 — animated login backdrop (V0.2.1)
// Picks one of three scenes at random on each load (never the same one twice in a row
// for the same browser), draws it on a full-bleed canvas behind the sign-in card.
// Pauses when the tab is hidden; still frame when the user prefers reduced motion.
// Force a scene for testing with ?bg=drawing | contours | structure.

import { useEffect, useRef, useState } from 'react';
import { makeScene, VARIANTS, type BackdropVariant } from './loginBackdrop/scenes';

const LAST_KEY = 'pmc3.loginBackdrop.last';

function pickVariant(): BackdropVariant {
  try {
    const forced = new URLSearchParams(window.location.search).get('bg');
    if (forced && (VARIANTS as string[]).includes(forced)) return forced as BackdropVariant;
  } catch { /* ignore */ }
  let last: string | null = null;
  try { last = window.localStorage.getItem(LAST_KEY); } catch { /* storage blocked */ }
  const pool = VARIANTS.filter((v) => v !== last);
  const v = pool[Math.floor(Math.random() * pool.length)];
  try { window.localStorage.setItem(LAST_KEY, v); } catch { /* storage blocked */ }
  return v;
}

export interface LoginBackdropProps {
  /** Override the random pick (e.g. from Admin settings later). */
  variant?: BackdropVariant;
  /** Corner readout for the contour scene; defaults to Tavarua's coordinates. */
  coords?: string;
}

export function LoginBackdrop({ variant, coords }: LoginBackdropProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [chosen] = useState<BackdropVariant>(() => variant ?? pickVariant());

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const scene = makeScene(chosen, { coords });
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let w = 0, h = 0, raf = 0, last = 0, hiddenAt = 0, hiddenTotal = 0;
    const start = performance.now();

    const resize = () => {
      const r = canvas.getBoundingClientRect();
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      w = r.width; h = r.height;
      canvas.width = Math.round(w * dpr); canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      scene.init(w, h);
      if (reduce) scene.draw(ctx, w, h, scene.stillTime);
    };

    const frame = (now: number) => {
      raf = requestAnimationFrame(frame);
      if (document.hidden || now - last < 1000 / scene.fps - 1) return;
      last = now;
      scene.draw(ctx, w, h, (now - start - hiddenTotal) / 1000);
    };

    const onVis = () => {
      if (document.hidden) hiddenAt = performance.now();
      else if (hiddenAt) { hiddenTotal += performance.now() - hiddenAt; hiddenAt = 0; }
    };

    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    resize();
    document.addEventListener('visibilitychange', onVis);
    if (!reduce) raf = requestAnimationFrame(frame);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [chosen, coords]);

  return (
    <div className="login-backdrop" aria-hidden="true" data-variant={chosen}>
      <canvas ref={canvasRef} />
      <div className="login-backdrop__vignette" />
    </div>
  );
}
